import React from 'react';

function PrivacyPolicy({ onClose }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          <h1 style={styles.h1}>Privacy Policy</h1>
          <p style={styles.meta}>Last updated: 16 October 2026</p>

          <p style={styles.p}>
            This Privacy Policy explains how ApptNote (apptnote.com), operated by
            an individual data controller based in Ireland, collects, uses and
            protects your personal data in accordance with the General Data
            Protection Regulation (GDPR), the Data Protection Acts 1988–2018,
            and the California Consumer Privacy Act (CCPA) where applicable.
          </p>

          <h2 style={styles.h2}>1. Who we are</h2>
          <p style={styles.p}>
            ApptNote is a web-based tool that helps users track symptoms and
            prepare summaries for medical appointments. We are based in Ireland
            and operate under EU data protection law. For data protection queries
            contact us at: <a href="mailto:privacy@apptnote.com" style={styles.link}>privacy@apptnote.com</a>
          </p>

          <h2 style={styles.h2}>2. What data we collect</h2>
          <p style={styles.p}>We collect the following personal data:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Email address</strong> — collected when you create an account, generate a report, or sign up for symptom tracking.</li>
            <li style={styles.li}><strong>Health information</strong> — the symptoms, severity ratings, mood, energy, sleep and stress scores, and any notes you enter into the symptom tracker. This data is stored securely and used to power your trends, patterns, and appointment reports.</li>
            <li style={styles.li}><strong>Generated report content</strong> — when you generate an appointment report, the AI-generated output is saved to your account so you can retrieve it later without re-generating. This content is encrypted at rest, is only accessible to you, and can be deleted at any time by requesting account deletion.</li>
            <li style={styles.li}><strong>Usage data</strong> — we store report count information to manage your plan usage.</li>
          </ul>

          <p style={styles.p}>
            <strong>What we do not collect:</strong> We do not collect diagnoses,
            prescriptions, or any data from your healthcare provider. We do not
            collect or store your doctor's appointment notes.
          </p>

          <h2 style={styles.h2}>3. How we use your data</h2>
          <ul style={styles.ul}>
            <li style={styles.li}>Your email address is used to manage your account and communicate service updates.</li>
            <li style={styles.li}>Your symptom data is sent to Anthropic's Claude API solely to generate your AI insights and doctor-ready reports. We do not use your health data for any other purpose.</li>
            <li style={styles.li}>Your symptom tracking history is stored securely to enable pattern detection, trend analysis, and generating reports based on your logged history.</li>
            <li style={styles.li}>We will never sell your data to third parties. Ever.</li>
          </ul>

          <h2 style={styles.h2}>4. AI processing and report storage</h2>
          <p style={styles.p}>
            ApptNote uses Anthropic's Claude AI in two places: the short daily messages you see
            after logging, and the appointment report. Both are clearly labelled as written by AI.
            To create them, structured parts of your symptom data (scores, symptom frequency,
            lifestyle tags, journey stage) are sent to Anthropic's API. Your personal notes and
            free-text entries are <strong>never</strong> sent to the AI, and neither is your email
            address. This data is used only to create your messages and report and is not used to
            train Anthropic's models under our API agreement. Anthropic deletes API inputs and
            outputs within 30 days, except where it must keep them longer to investigate misuse
            of its service or to comply with the law.
          </p>
          <p style={styles.p}>
            Once generated, your report is saved to your ApptNote account so you can view it
            again without re-generating. It is stored encrypted, linked only to your account,
            and is permanently deleted if you delete your account. Your report content is never
            shared with any third party, used for analytics, or accessed by ApptNote staff
            except to investigate a reported technical problem at your request.
          </p>
          <p style={styles.p}>
            AI-generated content describes what you logged, in your own words. It does not
            interpret your symptoms and is not a diagnosis, medical advice, or a substitute for
            professional medical judgment. Downloaded reports say they were drafted by AI, both on
            the page and in the file's details.
          </p>

          <h2 style={styles.h2}>5. Legal basis for processing</h2>
          <p style={styles.p}>
            We process your email address on the basis of <strong>contract</strong> —
            it is necessary to provide the service you have requested.
            We process your health information on the basis of your
            <strong> explicit consent</strong>, which you give by ticking the consent box when you
            sign in. We record when you gave it and which version of our terms applied. You may
            withdraw this consent at any time by contacting us or by deleting your account.
          </p>

          <h2 style={styles.h2}>6. Data sharing</h2>
          <p style={styles.p}>
            We never sell your data or share it for advertising. We share data with the following
            third-party processors only, each for the purpose described:
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Anthropic</strong> (USA) — structured symptom data is processed by Anthropic's Claude API to generate insights and reports. Anthropic acts as a data processor under a Data Processing Addendum (DPA) which includes Standard Contractual Clauses (SCCs) covering transfers outside the EEA. Anthropic's privacy policy: anthropic.com/privacy</li>
            <li style={styles.li}><strong>Supabase</strong> — our database provider, which stores your email address, symptom tracking data, and account information. Your data is stored in the EU (Ireland). Data is encrypted at rest (AES-256) and in transit (TLS). Supabase acts as a data processor under a signed DPA. Supabase's privacy policy: supabase.com/privacy</li>
            <li style={styles.li}><strong>Vercel</strong> (USA) — our hosting provider. The app's server functions run in the EU (Dublin, Ireland), close to our database. Vercel is a US company, so some service data (such as request logs) may be handled in the USA. Vercel is certified under the EU-US Data Privacy Framework, which covers those transfers. Vercel's privacy policy: vercel.com/legal/privacy-policy</li>
            <li style={styles.li}><strong>Resend</strong> (USA) — used to send sign-in codes and service emails, so it processes your email address. Transfers are covered by Standard Contractual Clauses. Resend's privacy policy: resend.com/legal/privacy-policy</li>
            <li style={styles.li}><strong>ImprovMX</strong> — forwards emails sent to our apptnote.com addresses (such as hello@ and privacy@) to our inbox. It processes the content of any email you send us. ImprovMX's privacy policy: improvmx.com</li>
            <li style={styles.li}><strong>Google (Gmail)</strong> — the inbox where we receive, store and reply to emails you send us. Google's privacy policy: policies.google.com/privacy</li>
            <li style={styles.li}><strong>Stripe</strong> — used to process subscription payments. Stripe handles payment card data directly and we never see or store your card details. Stripe's privacy policy: stripe.com/privacy</li>
          </ul>
          <p style={styles.p}>
            <strong>International transfers.</strong> Your account and symptom data are stored in
            the EU (Ireland). If you live outside the EU, for example in the USA, your data is still
            stored in Ireland and protected under EU GDPR. Where a processor handles data outside the EEA (mainly in the USA), the transfer
            is covered by the European Commission's Standard Contractual Clauses and, where the
            company is certified, the EU-US Data Privacy Framework. You can ask us for details of
            these safeguards at privacy@apptnote.com.
          </p>

          <h2 style={styles.h2}>7. Data retention</h2>
          <p style={styles.p}>
            We retain your account data and symptom tracking history for as long as your account
            remains active, or until you request deletion.
          </p>
          <p style={styles.p}>
            We delete inactive accounts so we don't hold health data longer than needed. If you
            create an account but log fewer than 3 days and don't sign in for 3 months, or if you
            don't sign in for 12 months, we'll email you. If you don't sign in within 30 days of
            that email, we'll permanently delete your account and data.
          </p>
          <p style={styles.p}>
            When you delete your account, all personal data — including your email address,
            symptom logs, AI insights, and reports — is permanently deleted from our systems
            within 30 days. Anonymised, non-identifiable aggregate statistics may be retained
            for product improvement.
          </p>

          <h2 style={styles.h2}>8. Data security</h2>
          <p style={styles.p}>Your data is protected by:</p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Encryption in transit</strong> — all data is transmitted over TLS-encrypted HTTPS connections.</li>
            <li style={styles.li}><strong>Encryption at rest</strong> — your data is stored with AES-256 encryption in Supabase's secure infrastructure.</li>
            <li style={styles.li}><strong>Access controls</strong> — your data is only accessible to your own account. We do not share data between users.</li>
            <li style={styles.li}><strong>No third-party data sales</strong> — we do not sell, rent, or trade your personal data.</li>
          </ul>
          <p style={styles.p}>
            While we take every reasonable step to protect your data, no system is completely
            secure. Please contact us immediately at privacy@apptnote.com if you suspect any
            unauthorised access to your account.
          </p>

          <h2 style={styles.h2}>9. Your rights under GDPR</h2>
          <p style={styles.p}>Under GDPR you have the right to:</p>
          <ul style={styles.ul}>
            <li style={styles.li}>Access the personal data we hold about you</li>
            <li style={styles.li}>Request correction of inaccurate data</li>
            <li style={styles.li}>Request deletion of your data (including all symptom tracking history)</li>
            <li style={styles.li}>Request export of your data in a portable format</li>
            <li style={styles.li}>Withdraw consent at any time</li>
            <li style={styles.li}>Object to processing based on legitimate interests</li>
            <li style={styles.li}>Lodge a complaint with the Data Protection Commission at dataprotection.ie</li>
          </ul>
          <p style={styles.p}>
            To exercise any of these rights email us at <a href="mailto:privacy@apptnote.com" style={styles.link}>privacy@apptnote.com</a> and
            we will respond within one month. We may ask you to verify your identity before processing your request.
          </p>

          <h2 style={styles.h2}>10. California residents (CCPA)</h2>
          <p style={styles.p}>
            If you are a California resident, you have additional rights under the California
            Consumer Privacy Act (CCPA):
          </p>
          <ul style={styles.ul}>
            <li style={styles.li}><strong>Right to know</strong> — you may request disclosure of the categories and specific pieces of personal information we collect, use, and share.</li>
            <li style={styles.li}><strong>Right to delete</strong> — you may request deletion of personal information we have collected from you, subject to certain exceptions.</li>
            <li style={styles.li}><strong>Right to opt out of sale</strong> — we do not sell your personal information. We do not share your data with third parties for their commercial purposes.</li>
            <li style={styles.li}><strong>Right to non-discrimination</strong> — we will not discriminate against you for exercising your CCPA rights.</li>
          </ul>
          <p style={styles.p}>
            To exercise your California rights, contact us at <a href="mailto:privacy@apptnote.com" style={styles.link}>privacy@apptnote.com</a>.
            We will respond within 45 days. California residents may also use an authorised agent to submit requests on their behalf.
          </p>

          <h2 style={styles.h2}>11. Cookies and local storage</h2>
          <p style={styles.p}>
            ApptNote uses your browser's local storage to manage your session. This data stays
            on your device and is not transmitted to our servers independently. You can clear it
            at any time by signing out or clearing your browser data. We do not use advertising
            cookies, analytics tools or any third-party tracking. Our fonts and other assets are
            served from our own site, so loading a page does not send your information to anyone else.
          </p>

          <h2 style={styles.h2}>12. Children's privacy</h2>
          <p style={styles.p}>
            ApptNote is not intended for individuals under the age of 18. We do not knowingly
            collect data from children. If you believe a child has provided us with personal
            data, please contact us at privacy@apptnote.com and we will delete it promptly.
          </p>

          <h2 style={styles.h2}>13. Changes to this policy</h2>
          <p style={styles.p}>
            We may update this policy as the service develops. We will notify registered users
            of significant changes by email at least 30 days before they take effect.
            The "Last updated" date at the top reflects the most recent changes.
          </p>
        </div>

        <button style={styles.closeButton} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(28, 42, 58, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '24px',
  },
  modal: {
    backgroundColor: '#FAF7F2',
    borderRadius: '16px',
    maxWidth: '640px',
    width: '100%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 32px',
    borderBottom: '1px solid #e1e8ed',
    position: 'sticky',
    top: 0,
    backgroundColor: '#FAF7F2',
    zIndex: 1,
  },
  logoAppt: { fontSize: '20px', fontWeight: '700', color: '#2C2C2A' },
  logoNote: { fontSize: '20px', fontWeight: '700', color: '#D4A5A5' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '18px',
    cursor: 'pointer', color: '#6c8396', padding: '4px 8px',
  },
  content: { padding: '32px' },
  h1: { fontSize: '28px', fontWeight: '800', color: '#2C2C2A', margin: '0 0 8px' },
  meta: { fontSize: '13px', color: '#6c8396', margin: '0 0 24px' },
  h2: { fontSize: '17px', fontWeight: '700', color: '#2C2C2A', margin: '28px 0 8px' },
  p: { fontSize: '14px', color: '#4a6070', lineHeight: '1.7', margin: '0 0 12px' },
  ul: { margin: '0 0 12px', paddingLeft: '20px' },
  li: { fontSize: '14px', color: '#4a6070', lineHeight: '1.7', marginBottom: '6px' },
  link: { color: '#7A9E87', textDecoration: 'underline' },
  closeButton: {
    width: 'calc(100% - 64px)',
    margin: '0 32px 32px',
    backgroundColor: '#2C2C2A',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'DM Sans', system-ui, sans-serif",
  },
};

export default PrivacyPolicy;
